/**
 * Summary view component
 */

import {useEffect, useState} from 'react';
import {useSummarizationStore} from '@voxapilot/shared';
import {api} from '../../config/firebase';
import {Loading} from '../ui/Loading';
import {EmptyState} from '../ui/EmptyState';
import type {ActionItem} from '@voxapilot/shared';

interface SummaryViewProps {
  meetingId: string;
}

export function SummaryView({meetingId}: SummaryViewProps) {
  const {getSummary, setSummary, setLoading, setError} = useSummarizationStore();
  const [isLoading, setIsLoading] = useState(true);
  const summary = getSummary(meetingId);

  useEffect(() => {
    loadSummary();
  }, [meetingId]);

  const loadSummary = async () => {
    try {
      setIsLoading(true);
      setLoading(meetingId, true);
      setError(meetingId, null);

      const response = await api.summarization.getSummary(meetingId);
      if (response.success && response.data) {
        setSummary(meetingId, response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to load summary');
      }
    } catch (err) {
      const error = err as Error;
      setError(meetingId, error.message);
    } finally {
      setIsLoading(false);
      setLoading(meetingId, false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loading size="md" />
      </div>
    );
  }

  if (!summary) {
    return (
      <EmptyState
        icon="📊"
        title="No summary available"
        description="The summary is being generated. Please check back in a few moments."
        size="sm"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Executive Summary */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
          Executive Summary
        </h3>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <p className="text-gray-900 dark:text-white">{summary.summary}</p>
        </div>
      </div>

      {/* Key Points */}
      {summary.keyPoints && summary.keyPoints.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Key Points
          </h3>
          <ul className="space-y-2">
            {summary.keyPoints.map((point, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
              >
                <span className="text-primary-600 dark:text-primary-400 mt-1">•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Items */}
      {summary.actionItems && summary.actionItems.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Action Items
          </h3>
          <div className="space-y-3">
            {summary.actionItems.map((item) => (
              <ActionItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Decisions */}
      {summary.decisions && summary.decisions.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Decisions Made
          </h3>
          <ul className="space-y-2">
            {summary.decisions.map((decision, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
              >
                <span className="text-green-600 dark:text-green-400 mt-1">✓</span>
                <span>{decision}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Next Steps */}
      {summary.nextSteps && summary.nextSteps.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Next Steps
          </h3>
          <ul className="space-y-2">
            {summary.nextSteps.map((step, index) => (
              <li
                key={index}
                className="flex items-start gap-2 text-gray-700 dark:text-gray-300"
              >
                <span className="text-blue-600 dark:text-blue-400 mt-1">→</span>
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ActionItemCard({item}: {item: ActionItem}) {
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-start justify-between mb-2">
        <p className="text-gray-900 dark:text-white font-medium">{item.text}</p>
        {item.priority && (
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
              item.priority
            )}`}
          >
            {item.priority}
          </span>
        )}
      </div>
      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
        {item.assignee && <span>Assigned to: {item.assignee}</span>}
        {item.dueDate && (
          <span>Due: {new Date(item.dueDate).toLocaleDateString()}</span>
        )}
        {item.status && (
          <span className="capitalize">Status: {item.status}</span>
        )}
      </div>
    </div>
  );
}

